import axios from "axios";
import useAuth from "./useAuth";
import { UserId } from "../data_models/helpers";
import { TASK_APPLICATION_ENDPOINTS } from "../config/apiEndpoints";
import { Application, IApplication } from "../data_models/application";
import { ITask } from "../data_models/task";
import { IProject } from "../data_models/project";

export interface CreateApplicationOptions {
    status: string;
    description: string;
    responseUser: UserId;
    responseDate: Date;
    confirmedOrRefusedAt: Date;
}


function useApplications() {

    const { authToken } = useAuth();

    const createApplication = async (projectId: string, taskId: string, description?: string): Promise<Application> => {
        try {
            const response = await axios.post(`${TASK_APPLICATION_ENDPOINTS.POST_APPLICATION}`,
                {
                    projectId,
                    taskId,
                    description,
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const createApplicationBulk = async (projectId: string, taskId: string, description?: string, expiresAt?: Date): Promise<Application> => {
        try {
            const response = await axios.post(`${TASK_APPLICATION_ENDPOINTS.POST_APPLICATION_BULK}`,
                {
                    projectId,
                    taskId,
                    description,
                    expiresAt,
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchApplicationByProjectId = async (projectId: string, page: number, limit: number, filter?: IApplication) => {
        try {
            const response = await axios.get(`${TASK_APPLICATION_ENDPOINTS.GET_APPLICATIONS_FOR_PROJECT}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    filter: filter ? filter : {},
                    page: page,
                    limit: limit
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchApplicationsForUser = async (page: number, limit: number, filter?: IApplication) => {
        try {
            const response = await axios.get(`${TASK_APPLICATION_ENDPOINTS.GET_APPLICATIONS_FOR_USER}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    filter: filter ? filter : {},
                    page: page,
                    limit: limit
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchAliveApplicationsAsPm = async (page: number, limit: number) => {
        try {
            const response = await axios.get(`${TASK_APPLICATION_ENDPOINTS.GET_ALIVE_APPLICATIONS_AS_PM}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const getNumberOfApplicationsForProject = async (projectId: string): Promise<number> => {
        try {
            const response = await axios.get(`${TASK_APPLICATION_ENDPOINTS.GET_NUMBER_OF_APPLICATIONS_FOR_PROJECT}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const acceptApplication = async (applicationId: string): Promise<Application> => {
        try {
            const response = await axios.put(`${TASK_APPLICATION_ENDPOINTS.PUT_APPLICATION_ACCEPT}/${applicationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });

            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const rejectApplication = async (applicationId: string): Promise<Application> => {
        try {
            const response = await axios.put(`${TASK_APPLICATION_ENDPOINTS.PUT_APPLICATION_REJECT}/${applicationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });

            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const confirmApplication = async (applicationId: string): Promise<{
        application: Application,
        task: ITask,
        project: IProject,
    }> => {
        try {
            const response = await axios.put(`${TASK_APPLICATION_ENDPOINTS.PUT_APPLICATION_CONFIRM}/${applicationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const refuseApplication = async (applicationId: string): Promise<{
        application: Application,
        task: ITask,
        project: IProject,
    }> => {
        try {
            const response = await axios.put(`${TASK_APPLICATION_ENDPOINTS.PUT_APPLICATION_REFUSE}/${applicationId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });

            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    return {
        createApplication,
        createApplicationBulk,
        fetchApplicationByProjectId,
        fetchApplicationsForUser,
        fetchAliveApplicationsAsPm,
        getNumberOfApplicationsForProject,
        acceptApplication,
        rejectApplication,
        confirmApplication,
        refuseApplication,
    };
}
export default useApplications;